import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthPanel } from "./LoginPage";
import { Spinner } from "@/components/ui/spinner";
import { useTheme } from "@/components/ThemeProvider";
import { useBrand } from "@/branding/BrandProvider";
import { exchangeCode } from "@/auth/exchange";
import { decodeJwtPayload } from "@/auth/session";

/** Portal redirects here with ?code=<jwt>. See eng-docs "AD - Sandbox Handoff Impersonation" §8. */
export function HandoffPage() {
  const [params] = useSearchParams();
  const code = params.get("code");
  const { theme } = useTheme();
  const brand = useBrand();
  const logo = theme === "dark" ? brand.logoDark : brand.logoLight;

  const [error, setError] = useState<string | null>(null);
  // Tokens are single-use; StrictMode re-runs effects, and a second /auth/exchange races the first (SQL 40001 500).
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (!code) {
      setError("This handoff link is missing a code. Go back to the portal and try again.");
      return;
    }
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;
    // No cancelled flag: StrictMode's cleanup would set it and, with the second run skipped above, hide every error.
    (async () => {
      try {
        const result = await exchangeCode(code);
        if (!result.ok) {
          // One code covers used, expired and invalid links (no oracle for whether a link exists). Only a demo link
          // (its unverified payload says so) points at a Dolfin contact; a portal handoff goes back to the portal.
          const demoLink = decodeJwtPayload(code)?.label === "demo";
          setError(
            result.code === "Auth.InvalidAuthCode" && demoLink
              ? "This link has been used. Ask your Dolfin contact for a new one."
              : (result.detail ??
                  (result.status === 401
                    ? "This handoff link is invalid or expired. Go back to the portal to start a new session."
                    : "Couldn't open this organisation. Try again.")),
          );
          return;
        }
        // Hard reload so AuthProvider rehydrates from localStorage.
        window.location.replace(result.landing);
      } catch {
        setError("Network error completing the handoff.");
      }
    })();
  }, [code]);

  return (
    <AuthPanel
      logo={logo}
      brandName={brand.name}
      title={error ? "This link didn't work" : "Opening organisation"}
      description={error ? undefined : "Exchanging your handoff code. This only takes a moment."}
      error={error}
    >
      {error ? (
        <a href="/login" className="text-sm font-medium underline-offset-4 hover:underline">
          Back to login
        </a>
      ) : (
        <Spinner className="size-5 text-muted-foreground" />
      )}
    </AuthPanel>
  );
}
